/**
 * ServicesTabbedPanel - Panel izquierdo con pestañas para módulo de servicios
 *
 * @version 1.0
 * @module core/modules/services
 *
 * @description
 * Pestañas: Red de agua, Cloacas, Electricidad, Gas, Telecomunicaciones, Medidores
 */

import { useState } from 'react';
import {
  Droplets, Pipette, Zap, Flame, Wifi, Gauge,
} from 'lucide-react';
import { BaseInventoryTab } from '../../layouts/panels/LeftPanel/BaseInventoryTab';
import { LeftPanelToggle } from '../../layouts/panels/LeftPanel/LeftPanel';
import { useRightPanel } from '../../layouts/panels/RightPanel/RightPanelContext';
import { useMapTools } from '../../map-tools';
import { useServicesElementColorsStore } from './store/element-colors-store';
import { MAPPABLE_ELEMENT_IDS } from './constants/element-colors';
import type { ServicesElementId } from './constants/element-colors';
import '../../layouts/panels/LeftPanel/tabbed-left-panel.css';

type ServicesTabType =
  | 'water'
  | 'sewage'
  | 'electricity'
  | 'gas'
  | 'telecom'
  | 'meters';

interface TabInfo {
  id: ServicesTabType;
  icon: React.ReactNode;
  tooltip: string;
  title: string;
  emptyMessage: string;
}

const TABS: TabInfo[] = [
  { id: 'water',       icon: <Droplets size={16} />, tooltip: 'Red de agua potable', title: 'Red de agua',        emptyMessage: 'Sin elementos de red de agua' },
  { id: 'sewage',      icon: <Pipette size={16} />,  tooltip: 'Red cloacal',         title: 'Cloacas',            emptyMessage: 'Sin elementos de red cloacal' },
  { id: 'electricity', icon: <Zap size={16} />,      tooltip: 'Red eléctrica',       title: 'Electricidad',       emptyMessage: 'Sin elementos eléctricos' },
  { id: 'gas',         icon: <Flame size={16} />,    tooltip: 'Red de gas',          title: 'Gas',                emptyMessage: 'Sin elementos de red de gas' },
  { id: 'telecom',     icon: <Wifi size={16} />,     tooltip: 'Telecomunicaciones',  title: 'Telecomunicaciones', emptyMessage: 'Sin elementos de telecomunicaciones' },
  { id: 'meters',      icon: <Gauge size={16} />,    tooltip: 'Medidores',           title: 'Medidores',          emptyMessage: 'Sin medidores registrados' },
];

export function ServicesTabbedPanel(): JSX.Element {
  const [activeTab, setActiveTab] = useState<ServicesTabType>('water');
  const { canChangeContent } = useRightPanel();
  const { selectElement, getSelectedElement } = useMapTools();
  const getColor = useServicesElementColorsStore((s) => s.getColor);
  const setColor = useServicesElementColorsStore((s) => s.setColor);

  const activeTabInfo = TABS.find(t => t.id === activeTab)!;
  const isMappable = MAPPABLE_ELEMENT_IDS.has(activeTab);
  const selectionKey = `services-${activeTab}`;
  const activeElementId = getSelectedElement(selectionKey);

  const handleCodeClick = async (elementId: string): Promise<void> => {
    console.log(`[ServicesTabbedPanel] Elemento clickeado: ${activeTab} - ${elementId}`);
    const canChange = await canChangeContent();
    if (!canChange) return;

    // Marcar elemento como seleccionado para resaltado en sidebar
    selectElement(selectionKey, elementId);

    console.log(`[ServicesTabbedPanel] Despachando evento: services-${activeTab}-select`);
    window.dispatchEvent(new CustomEvent(`services-${activeTab}-select`, {
      detail: { elementId, elementType: activeTab },
    }));
  };

  return (
    <div className="tabbed-left-panel">
      <div className="tabs-header">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            title={tab.tooltip}
            aria-label={tab.tooltip}
          >
            {tab.icon}
          </button>
        ))}
      </div>

      <div className="content-card-wrapper">
        <div className="content-card">
          <div className="tab-content">
            <BaseInventoryTab
              key={activeTab}
              title={activeTabInfo.title}
              emptyMessage={activeTabInfo.emptyMessage}
              eventPrefix={`services-${activeTab}`}
              moduleId="services"
              elementColor={isMappable ? getColor(activeTab as ServicesElementId) : undefined}
              onElementColorChange={isMappable ? (hex) => setColor(activeTab as ServicesElementId, hex) : undefined}
              onCodeClick={handleCodeClick}
              activeElementId={activeElementId}
            />
          </div>
          <LeftPanelToggle />
        </div>
      </div>
    </div>
  );
}
